import React from 'react'
import styled from '@emotion/styled'
import type { LinkInfo } from '@mobi/betslip/Components/Common/BetCard/types'
import { Price } from '@mobi/betslip/Components/Common/Price'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'
import { Currency } from '@mobi/utils/money'
import { toTitleCase } from '@mobi/utils/string'
import { LinkWrapper } from '../Common/LinkWrapper'

export const SelectionInfo: React.FC<{
  heading: React.ReactNode
  subHeading: React.ReactNode
  iconComponent?: React.ReactNode
  rightAlignedComponent?: React.ReactNode
  linkInfo?: LinkInfo
  priceWin?: string | number | null
  priceWinBoosted?: number | null
  priceWinLastSeen?: number | null
  pricePlace?: string | number | null
  pricePlaceBoosted?: string | number | null
  pricePlaceLastSeen?: number | null
  bottomComponent?: React.ReactNode
}> = ({
  heading,
  subHeading,
  priceWin,
  priceWinLastSeen,
  pricePlace,
  priceWinBoosted,
  pricePlaceBoosted,
  pricePlaceLastSeen,
  iconComponent,
  rightAlignedComponent,
  linkInfo,
  bottomComponent,
}) => {
  const priceWinMoney = new Currency(priceWin || 0)
  const priceWinBoostedMoney = priceWinBoosted && new Currency(priceWinBoosted || 0)
  const pricePlaceMoney = new Currency(pricePlace || 0)
  const pricePlaceBoostedMoney = pricePlaceBoosted && new Currency(pricePlaceBoosted || 0)

  const hasWinValue = priceWinMoney.value > 0
  const hasPlaceValue = pricePlaceMoney.value > 0

  return (
    <LinkWrapper linkInfo={linkInfo}>
      <WrapperStyled
        data-testid='BetCard.SelectionInfo'
        style={
          bottomComponent
            ? {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            : undefined
        }
      >
        <div>
          {iconComponent && <span>{iconComponent}</span>}
          <h3>
            <span>{typeof heading === 'string' ? toTitleCase(heading) : heading}</span>

            <span>{subHeading}</span>
          </h3>
        </div>

        <div>
          <span>
            {hasWinValue && (
              <Price
                value={priceWinMoney.value}
                valueLastSeen={priceWinLastSeen}
                shouldDisplayAsBoosted={false}
                isScratchedOut={!!priceWinBoostedMoney && priceWinBoostedMoney.value > 0}
              />
            )}

            {priceWinBoostedMoney && (
              <Price
                value={priceWinBoostedMoney.value}
                valueLastSeen={priceWinLastSeen}
                shouldDisplayAsBoosted={true}
              />
            )}
          </span>

          {(hasWinValue || priceWinBoostedMoney) && (hasPlaceValue || pricePlaceBoostedMoney) && (
            <span> / </span>
          )}

          <span>
            {hasPlaceValue && (
              <Price
                value={pricePlaceMoney.value}
                valueLastSeen={pricePlaceLastSeen}
                shouldDisplayAsBoosted={false}
                isScratchedOut={!!pricePlaceBoostedMoney && pricePlaceBoostedMoney.value > 0}
              />
            )}

            {pricePlaceBoostedMoney && (
              <Price
                value={pricePlaceBoostedMoney.value}
                valueLastSeen={pricePlaceLastSeen}
                shouldDisplayAsBoosted={true}
              />
            )}
          </span>
        </div>

        {rightAlignedComponent && <div>{rightAlignedComponent}</div>}
      </WrapperStyled>
      {bottomComponent && <div>{bottomComponent}</div>}
    </LinkWrapper>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.sm,
  padding: spacing.smx1,
  borderRadius: radius.md,
  backgroundColor: colors.surface[200],

  '> div': {
    display: 'flex',
    alignItems: 'center',
  },

  // Selection text info + icon
  '> div:nth-of-type(1)': {
    flex: 1,

    '> span': {
      alignSelf: 'flex-start',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '3.6rem',
      height: '3.6rem',
      marginRight: spacing.sm,
      borderRadius: radius.smx1,
      backgroundColor: colors.surface[400],
    },

    '> h3': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      padding: 0,
      margin: 0,
      background: 0,
      textTransform: 'none',

      '> span:first-of-type': {
        display: 'flex',
        flexWrap: 'wrap',
        fontSize: font.size.lg.fontSize,
        fontWeight: font.weight.medium,
        letterSpacing: font.size.lg.letterSpacing,
        lineHeight: font.size.lg.lineHeight,
        color: colors.black,
      },

      '> span:last-of-type': {
        fontSize: font.size.md.fontSize,
        fontWeight: font.weight.regular,
        letterSpacing: font.size.md.letterSpacing,
        lineHeight: font.size.md.lineHeight,
        color: colors.neutral[800],
      },
    },
  },

  // Price
  '> div:nth-of-type(2)': {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: spacing.smx2,
    marginLeft: 'auto',

    span: {
      display: 'inline-flex',
      gap: spacing.smx1,
      fontSize: font.size.lg.fontSize,
      fontWeight: font.weight.medium,
      letterSpacing: font.size.lg.letterSpacing,
      lineHeight: font.size.lg.lineHeight,
      color: colors.black,
    },
  },

  // Right Aligned Content
  '> div:nth-of-type(3):empty': {
    display: 'none',
  },
})
